import React, { useEffect, useState } from "react";
import {
    Card, CardHeader, CardContent, FormControl, InputLabel, Select, MenuItem,
    Button, Typography, LinearProgress, Snackbar, WithStyles, withStyles, createStyles, Theme,
} from "@material-ui/core";
import { ApiBackend } from "../../providers/apibackend";
import { Event } from "../../model/Event";

const ICE_EVENT_SETTING = "ice_event";

/**
 * Väljer vilket evenemang ICE-vyerna arbetar mot:
 *  - Dashboardens "ICE-information per lopp"
 *  - ICE-sökningen (ice.vasaloppet.se)
 * Lagras som CustomSetting "ice_event" (event-id).
 */
const IceEventSetting = ({ classes }: WithStyles) => {
    const backend = new ApiBackend();
    const [events, setEvents] = useState<Event[]>([]);
    const [selected, setSelected] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        (async () => {
            const [evs, settings] = await Promise.all([backend.listEvents(), backend.listCustomSettings()]);
            setEvents((evs || []).slice().sort((a, b) => (a.startDate < b.startDate ? 1 : -1)));
            const current = (settings || []).find(s => s.setting === ICE_EVENT_SETTING);
            setSelected(current?.value ?? "");
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async () => {
        setSaving(true);
        const ok = await backend.addOrUpdateCustomSetting({
            setting: ICE_EVENT_SETTING,
            value: selected,
            type: "event",
            updated: new Date(),
            updatedBy: "",
            isArchived: false,
        });
        setSaving(false);
        if (ok) { setSaved(true); }
    };

    return (
        <Card>
            <CardHeader className={classes.cardHeader} title="ICE-inställningar" />
            <CardContent>
                <Typography variant="body2" style={{ marginBottom: 12 }}>
                    Välj vilket evenemang ICE-vyerna ska peka på. Det styr både dashboardens
                    <b> ICE-information per lopp</b> och <b>sökningen av ICE-information</b> (ice.vasaloppet.se).
                    Lämnas det tomt används automatiskt den aktuella evenemangsveckan.
                </Typography>

                {loading ? <LinearProgress color="primary" /> :
                    <>
                        <FormControl variant="outlined" size="small" style={{ minWidth: 340 }}>
                            <InputLabel id="ice-event-label">Evenemang för ICE</InputLabel>
                            <Select
                                labelId="ice-event-label"
                                label="Evenemang för ICE"
                                value={selected}
                                onChange={(ev) => setSelected(ev.target.value as string)}
                            >
                                <MenuItem value=""><em>Automatiskt (aktuell evenemangsvecka)</em></MenuItem>
                                {events.map(e =>
                                    <MenuItem key={e.id} value={e.id}>
                                        {e.name}{e.startDate ? ` (${e.startDate})` : ""}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <div style={{ marginTop: 16 }}>
                            <Button variant="contained" color="primary" onClick={save} disabled={saving}>
                                {saving ? "Sparar…" : "Spara"}
                            </Button>
                        </div>
                    </>
                }
            </CardContent>
            <Snackbar open={saved} autoHideDuration={3000} onClose={() => setSaved(false)} message="ICE-evenemang sparat" />
        </Card>
    );
};

const useStyles = ({ palette }: Theme) => createStyles({
    cardHeader: { background: palette.secondary.main, color: palette.secondary.contrastText, padding: 3 },
});

export default withStyles(useStyles)(IceEventSetting);
